import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  FormHelperText,
  OutlinedInput,
  Tooltip,
  Button,
  Divider,
  Grid,
} from '@mui/material';
import {
  AttachMoney,
  CalendarMonth,
  Layers,
  Category as CategoryIcon,
  ShoppingBag,
  Star,
  EmojiEvents,
  TrendingUp,
  LocalOffer,
  Description,
  ArrowForward,
} from '@mui/icons-material';
import { Axios } from 'services/axios';
import { ENTITY_CONFIG } from 'config/entityConfig';
import { useSubscriptionPricing } from 'context/SubscriptionPricingContext';

const ModularEntitySelector = ({
  entityType,
  context = 'FAQ',
  selectedIds = [],
  onChange,
  disabled = false,
  error = null,
  helperText = null,
  required = false,
  showDescription = false,
  sx = {},
  selectionType = null,
  placeholder = null,
  size = 'medium',
  variant = 'outlined',
  multiple = false,
  readOnly = false,
  returnFullObjects = false,
  duration = null,
  country = null,
  durationId = null,
  countryId = null,
  durationDisplay = null,
  countryDisplay = null
}) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const { storeEntityMetadata } = useSubscriptionPricing();

  const finalDuration = duration || durationId;
  const finalCountry = country || countryId;

  const config = ENTITY_CONFIG[context]?.[entityType];
  
  const getConfig = () => {
    if (config) return config;
    
    // Try to find config with case-insensitive matching
    const contextConfig = ENTITY_CONFIG[context];
    if (!contextConfig) return null;
    
    // Try exact uppercase
    const upperKey = Object.keys(contextConfig).find(key => 
      key.toUpperCase() === entityType.toUpperCase()
    );
    
    if (upperKey) return contextConfig[upperKey];
    
    // Try to match by name property
    const byName = Object.values(contextConfig).find(item => 
      item.name?.toUpperCase() === entityType.toUpperCase()
    );
    
    return byName || null;
  };

  const entityConfig = getConfig();

  const { label, selectionType: configSelectionType, endpoint, plural, description } = entityConfig;

  const finalSelectionType = selectionType || 
    (multiple ? 'multiple' : configSelectionType || 'single');

  useEffect(() => {
    const fetchEntities = async () => {
      if (!endpoint) {
        console.warn(`No endpoint for ${context}.${entityType}`);
        return;
      }

      setLoading(true);
      setFetchError(null);
      try {
        let finalEndpoint = endpoint;

        // Build query parameters
        const queryParams = new URLSearchParams();
        
        // Always add context for subscription
        if (context === 'SUBSCRIPTION') {
          queryParams.append('context', 'SUBSCRIPTION');
        }
        
        // Add duration parameter if provided
        if (finalDuration) {
          queryParams.append('durationId', finalDuration);
        }
        
        // Add country parameter if provided
        if (finalCountry) {
          queryParams.append('countryId', finalCountry);
        }

        // Only add query string if we have parameters
        if (queryParams.toString()) {
          finalEndpoint = `${endpoint}?${queryParams.toString()}`;
        }

        const response = await Axios.fetch(finalEndpoint);       
        setEntities(response?.data?.data || []);
        
        console.log(`Loaded ${response?.data?.data?.length || 0} ${entityType} entities:`, response?.data?.data);
        
      } catch (err) {
        console.error(`❌ Error loading ${plural}:`, err);
        setFetchError(`Failed to load ${plural}. Please try again.`);
        setEntities([]);
      } finally {
        setLoading(false);
      }
    };

    if (entityType && endpoint) {
      fetchEntities();
    }
  }, [entityType, endpoint, plural, context, finalDuration, finalCountry, retryCount]);

  if (!entityConfig) {
    return (
      <Alert severity="error" sx={{ mt: 1, ...sx }}>
        Entity type "{entityType}" not found in {context} configuration.
        Available types: {Object.keys(ENTITY_CONFIG[context] || {}).join(', ')}
      </Alert>
    );
  }

  const storeMetadata = (entity) => {
    if (entity) {
      storeEntityMetadata(entity.id, {
        ...entity,
        id: entity.id,
        // Mappings (old + new model) have no top-level name — fall back to the mapping display name
        // so saved configs and chips show "Mapping for X" instead of the raw UUID.
        name:
          entity.name ||
          entity.title ||
          entity.displayInfo?.mappingName ||
          (entity.questionnaire?.name ? `Mapping for ${entity.questionnaire.name}` : undefined),
        description: entity.description,
        pricingSummary: entity.pricingSummary,
        displayInfo: entity.displayInfo,
      });
    }
  };

  const handleMultipleChange = (selectedValues) => {
    if (readOnly) return;
    
    // Store metadata for all selected entities
    selectedValues.forEach(value => {
      const entity = entities.find(e => e.id === value);
      if (entity) {
        storeMetadata(entity);
      }
    });
    
    if (returnFullObjects) {
      const selectedEntities = entities.filter(entity => selectedValues.includes(entity.id));
      onChange(selectedValues, selectedEntities);
    } else {
      onChange(selectedValues);
    }
  };

  const handleDeleteChip = (valueToDelete) => {
    if (readOnly) return;
    const newSelected = selectedIds.filter(id => id !== valueToDelete);
    
    if (returnFullObjects) {
      const selectedEntities = entities.filter(entity => newSelected.includes(entity.id));
      onChange(newSelected, selectedEntities);
    } else {
      onChange(newSelected);
    }
  };

  const handleSingleSelection = (value) => {
    if (readOnly) return;
    
    // Store entity metadata when selected
    if (value) {
      const selectedEntity = entities.find(e => e.id === value);
      storeMetadata(selectedEntity);
    }
    
    if (returnFullObjects) {
      if (!value) {
        onChange([], []);
      } else {
        const selectedEntity = entities.find(e => e.id === value);
        onChange([value], selectedEntity ? [selectedEntity] : []);
      }
    } else {
      // For single selection, always pass an array with one item or empty array
      if (!value) {
        onChange([]);
      } else {
        onChange([value]);
      }
    }
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return (
      entity?.name ||
      entity?.title ||
      entity?.displayInfo?.mappingName ||
      (entity?.questionnaire?.name ? `Mapping for ${entity.questionnaire.name}` : null) ||
      entityId
    );
  };

  const getEntityDetails = (entityId) => {
    return entities.find(e => e.id === entityId);
  };

  const renderValuePackDisplay = (entity) => {
    const baseInfo = (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {entity.name || entity.title || 'Unnamed'}
      </Typography>
    );

    if (!entity.tiers || entity.tiers.length === 0) {
      return (
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center" gap={1}>
            {baseInfo}
            {entity.featured && <Star fontSize="small" color="warning" />}
          </Box>
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            No pricing tiers available
          </Typography>
        </Box>
      );
    }

    // Get currency from first tier
    const currencySymbol = entity.tiers[0]?.currencySymbol || '$';
    
    // Sort tiers by tierType or displayOrder
    const sortedTiers = [...entity.tiers].sort((a, b) => {
      const tierOrder = { 'Low': 1, 'Mid': 2, 'High': 3 };
      return (tierOrder[a.tierType] || 0) - (tierOrder[b.tierType] || 0);
    });

    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          {baseInfo}
          {entity.featured && <Star fontSize="small" color="warning" />}
        </Box>
        
        {/* Show all tier prices */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          {sortedTiers.map((tier, index) => {
            const isPopular = tier.isPopular;
            const isRecommended = tier.isRecommended;
            
            return (
              <Box 
                key={tier.id || index}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 0.5,
                  bgcolor: isPopular ? 'rgba(255, 193, 7, 0.1)' : 
                           isRecommended ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                  borderRadius: 0.5,
                  border: isPopular ? '1px solid rgba(255, 193, 7, 0.3)' :
                          isRecommended ? '1px solid rgba(76, 175, 80, 0.3)' : 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {tier.tierType}
                  </Typography>
                  {isPopular && (
                    <Tooltip title="Most Popular">
                      <EmojiEvents fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                  {isRecommended && (
                    <Tooltip title="Recommended">
                      <TrendingUp fontSize="small" color="success" />
                    </Tooltip>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Original price with discount */}
                  {tier.discountType && tier.discountValue && tier.originalPrice && (
                    <Tooltip title="Original Price">
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          textDecoration: 'line-through',
                          color: 'text.disabled',
                          mr: 0.5
                        }}
                      >
                        {currencySymbol}{parseFloat(tier.originalPrice).toFixed(2)}
                      </Typography>
                    </Tooltip>
                  )}
                  
                  {/* Current price */}
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {currencySymbol}{parseFloat(tier.currentPrice).toFixed(2)}
                  </Typography>
                  
                  {/* Discount percentage */}
                  {tier.discountType === 'percentage' && tier.discountValue && (
                    <Chip
                      size="small"
                      label={`${tier.discountValue}% OFF`}
                      color="error"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        
        {/* Additional info chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {/* Duration info */}
          {entity.tiers[0]?.duration && (
            <Tooltip title={`${entity.tiers[0].duration} Month Plan`}>
              <Chip
                size="small"
                icon={<CalendarMonth fontSize="small" />}
                label={`${entity.tiers[0].duration}m`}
                color="primary"
                variant="outlined"
              />
            </Tooltip>
          )}
          
          {/* Country info */}
          {entity.tiers[0]?.countryName && (
            <Tooltip title={entity.tiers[0].countryName}>
              <Chip
                size="small"
                label={entity.tiers[0].countryCode || entity.tiers[0].countryName}
                color="secondary"
                variant="outlined"
              />
            </Tooltip>
          )}
          
          {/* Tier count */}
          <Tooltip title="Number of tiers available">
            <Chip
              size="small"
              icon={<Layers fontSize="small" />}
              label={`${entity.tiers.length} tiers`}
              color="info"
              variant="outlined"
            />
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderProductDisplay = (entity) => {
    const baseInfo = (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {entity.name || entity.title || 'Unnamed'}
      </Typography>
    );

    // Check if product has pricing info
    const hasPricing = entity.pricingSummary?.hasPackages || entity.pricingSummary?.priceRange;
    
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          {baseInfo}
          {entity.featured && <Star fontSize="small" color="warning" />}
          {entity.active === false && (
            <Chip label="Inactive" size="small" color="error" />
          )}
        </Box>
        
        {/* Product pricing info */}
        {hasPricing ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            {/* Price range if available */}
            {entity.pricingSummary?.priceRange && (
              <Tooltip title="Price range">
                <Chip
                  size="small"
                  icon={<AttachMoney fontSize="small" />}
                  label={`${entity.pricingSummary.priceRange.min} - ${entity.pricingSummary.priceRange.max}`}
                  color="success"
                  variant="outlined"
                />
              </Tooltip>
            )}
            
            {/* Package count */}
            {entity.pricingSummary?.packageCount && (
              <Tooltip title="Package count">
                <Chip
                  size="small"
                  icon={<ShoppingBag fontSize="small" />}
                  label={`${entity.pricingSummary.packageCount} packages`}
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
            )}
            
            {/* Category info */}
            {entity.displayInfo?.categoryName && (
              <Tooltip title="Category">
                <Chip
                  size="small"
                  icon={<CategoryIcon fontSize="small" />}
                  label={entity.displayInfo.categoryName}
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            Pricing not configured
          </Typography>
        )}
        
        {/* Description */}
        {entity.description && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            {entity.description.length > 80 
              ? `${entity.description.substring(0, 80)}...`
              : entity.description}
          </Typography>
        )}
      </Box>
    );
  };

  const renderPlanDisplay = (entity) => {
    console.log("entity in subs : ", entity);
    const baseInfo = (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {entity.displayInfo?.mappingName || entity.name || 'Mapping Package'}
      </Typography>
    );

    // Build question-answer pairs (EXACTLY like ViewMappingModal)
    const buildQuestionAnswerPairs = () => {
      const pairs = [];
      const answerData = entity.answerCombination || {};
      
      for (const [questionId, answerIds] of Object.entries(answerData)) {
        const question = entity.questions?.find(q => q.id === questionId);
        
        if (!question) {
          console.warn('Question not found in entity:', questionId);
          continue;
        }

        const answerTexts = [];
        for (const answerId of answerIds) {
          const option = question.options?.find(opt => opt.id === answerId);
          if (option) {
            answerTexts.push(option.answerText);
          } else {
            answerTexts.push(`Unknown (${answerId})`);
          }
        }

        pairs.push({
          questionId: question.id,
          questionText: question.text,
          questionType: question.type,
          answerIds: answerIds,
          answerTexts: answerTexts,
        });
      }

      return pairs;
    };

    const questionAnswerPairs = buildQuestionAnswerPairs();
    
    // Get recommended plans
    const recommendedPlans = entity.recommendedPlans || {};

    return (
      <Box display="flex" flexDirection="column" width="100%">
        {/* Mapping header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            {baseInfo}
            {entity.active === false && (
              <Chip label="Inactive" size="small" color="error" />
            )}
          </Box>
          
          {/* Stats chips */}
          <Box display="flex" gap={0.5}>
            <Tooltip title={`${questionAnswerPairs.length} questions`}>
              <Chip
                size="small"
                label={`${questionAnswerPairs.length}Q`}
                color="info"
                variant="outlined"
              />
            </Tooltip>
            <Tooltip title={`${entity.plans?.length || 0} plans`}>
              <Chip
                size="small"
                label={`${entity.plans?.length || 0}P`}
                color="primary"
                variant="outlined"
              />
            </Tooltip>
            {Object.keys(recommendedPlans).length > 0 && (
              <Tooltip title={`${Object.keys(recommendedPlans).length} recommended`}>
                <Chip
                  size="small"
                  icon={<Star fontSize="small" />}
                  label={Object.keys(recommendedPlans).length}
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {/* Questionnaire info */}
        {entity.questionnaire && (
          <Typography variant="caption" color="primary" sx={{ mb: 1 }}>
            {entity.questionnaire.name}
          </Typography>
        )}
        
        {/* COMPACT VISUAL FLOW (like ViewMappingModal but smaller) */}
        <Box 
          sx={{ 
            mt: 1, 
            p: 1.5, 
            bgcolor: '#f8f9fa', 
            borderRadius: 1, 
            border: '1px solid #e0e0e0' 
          }}
        >
          <Grid container spacing={1} alignItems="center">
            {/* IF User Selects */}
            <Grid item xs={5}>
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="primary" display="block" mb={1}>
                  IF User Selects:
                </Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                  {questionAnswerPairs.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No answers
                    </Typography>
                  ) : (
                    questionAnswerPairs.slice(0, 3).map((pair, index) => (
                      <Box key={pair.questionId} mb={1}>
                        <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">
                          Q{index + 1}: {pair.questionText.length > 30 
                            ? `${pair.questionText.substring(0, 30)}...` 
                            : pair.questionText}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.3} mt={0.3}>
                          {pair.answerTexts.slice(0, 2).map((text, idx) => (
                            <Chip
                              key={idx}
                              label={text.length > 15 ? `${text.substring(0, 15)}...` : text}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          ))}
                          {pair.answerTexts.length > 2 && (
                            <Chip
                              label={`+${pair.answerTexts.length - 2}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                  {questionAnswerPairs.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      +{questionAnswerPairs.length - 3} more questions
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            
            {/* Arrow */}
            <Grid item xs={2}>
              <Box display="flex" justifyContent="center">
                <ArrowForward sx={{ fontSize: 24, color: 'info.main' }} />
              </Box>
            </Grid>
            
            {/* THEN Recommend */}
            <Grid item xs={5}>
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="success" display="block" mb={1}>
                  THEN Recommend:
                </Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                  {(!entity.plans || entity.plans.length === 0) ? (
                    <Typography variant="caption" color="text.secondary">
                      No plans
                    </Typography>
                  ) : (
                    entity.plans.slice(0, 3).map((plan) => {
                      // Check if this plan is recommended
                      const planDurationId = plan.duration?.id || plan.durationId;
                      const isRecommended = recommendedPlans[planDurationId] === plan.id;
                      
                      return (
                        <Box 
                          key={plan.id} 
                          sx={{ 
                            mb: 1,
                            p: 0.5,
                            border: '1px solid',
                            borderColor: isRecommended ? 'warning.main' : 'success.main',
                            borderRadius: 0.5,
                            bgcolor: isRecommended ? 'rgba(255, 167, 38, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                            position: 'relative'
                          }}
                        >
                          {isRecommended && (
                            <Star 
                              fontSize="small" 
                              sx={{ 
                                position: 'absolute', 
                                top: 2, 
                                right: 2,
                                color: 'warning.main',
                                fontSize: '0.8rem'
                              }} 
                            />
                          )}
                          
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Chip 
                              label={plan.tier ? plan.tier.charAt(0).toUpperCase() : 'N/A'}
                              size="small"
                              color={isRecommended ? 'warning' : 'success'}
                              sx={{ 
                                height: 18, 
                                fontSize: '0.6rem',
                                fontWeight: 'bold'
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                              {plan.name.length > 20 ? `${plan.name.substring(0, 20)}...` : plan.name}
                            </Typography>
                          </Box>
                          
                          {/* Price info if available */}
                          {plan.prices && plan.prices.length > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3, fontSize: '0.65rem' }}>
                              {plan.prices[0].country?.currencySymbol || '$'}
                              {parseFloat(plan.prices[0].finalPrice).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  )}
                  {entity.plans && entity.plans.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      +{entity.plans.length - 3} more plans
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        {/* Additional info */}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {/* Duration info */}
          {entity.plans && entity.plans[0]?.duration && (
            <Tooltip title={`Duration: ${entity.plans[0].duration.value} ${entity.plans[0].duration.unit}`}>
              <Chip
                size="small"
                icon={<CalendarMonth fontSize="small" />}
                label={`${entity.plans[0].duration.value}${entity.plans[0].duration.unit.charAt(0)}`}
                color="info"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          
          {/* Country info if available */}
          {entity.plans && entity.plans[0]?.prices && entity.plans[0].prices.length > 0 && 
          entity.plans[0].prices[0]?.country && (
            <Tooltip title={entity.plans[0].prices[0].country.countryName}>
              <Chip
                size="small"
                label={entity.plans[0].prices[0].country.countryCode}
                color="secondary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          
          {/* Recommended info */}
          {Object.keys(recommendedPlans).length > 0 && (
            <Tooltip title={`${Object.keys(recommendedPlans).length} recommended plans`}>
              <Chip
                size="small"
                icon={<Star fontSize="small" />}
                label="Recommended"
                color="warning"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderGenericDisplay = (entity) => {
    const baseInfo = (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {entity.name || entity.title || 'Unnamed'}
      </Typography>
    );

    return (
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" gap={1}>
          {baseInfo}
          {entity.active === false && (
            <Chip label="Inactive" size="small" color="error" />
          )}
        </Box>
        
        {entity.description && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            {entity.description.length > 100 
              ? `${entity.description.substring(0, 100)}...`
              : entity.description}
          </Typography>
        )}
      </Box>
    );
  };

  const renderEntityDisplay = (entity) => {
    // For SUBSCRIPTION context with different entity types
    if (context === 'SUBSCRIPTION') {
      switch (entityType.toUpperCase()) {
        case 'VALUEPACK':
          return renderValuePackDisplay(entity);
        case 'PRODUCT':
          return renderProductDisplay(entity);
        case 'PLAN':
          return renderPlanDisplay(entity);
        default:
          return renderGenericDisplay(entity);
      }
    }
    
    // For FAQ/TESTIMONIAL contexts
    return renderGenericDisplay(entity);
  };

  // Function to render selected chip based on entity type
  const renderSelectedChip = (value) => {
    const entity = getEntityDetails(value);
    const entityName = getEntityName(value);
    
    // For SUBSCRIPTION context
    if (context === 'SUBSCRIPTION') {
      switch (entityType.toUpperCase()) {
        case 'VALUEPACK':
          if (entity?.tiers && entity.tiers.length > 0) {
            const currencySymbol = entity.tiers[0]?.currencySymbol || '$';
            const lowTier = entity.tiers.find(t => t.tierType === 'Low') || entity.tiers[0];
            const startingPrice = lowTier?.currentPrice ? parseFloat(lowTier.currentPrice).toFixed(2) : '0.00';
            
            return (
              <Tooltip title={`${entityName} - Starting from ${currencySymbol}${startingPrice}`}>
                <Chip
                  key={value}
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Layers fontSize="small" />
                      <span>{entityName}</span>
                      <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        {currencySymbol}{startingPrice}
                      </Typography>
                    </Box>
                  }
                  size="small"
                  onDelete={readOnly ? undefined : () => handleDeleteChip(value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ 
                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                    border: '1px solid #2e7d32',
                    '& .MuiChip-deleteIcon': {
                      color: readOnly ? 'rgba(0, 0, 0, 0.26)' : '#2e7d32',
                      '&:hover': {
                        color: readOnly ? undefined : '#1b5e20',
                        backgroundColor: 'rgba(46, 125, 50, 0.2)'
                      }
                    }
                  }}
                />
              </Tooltip>
            );
          }
          break;
          
        case 'PRODUCT':
          if (entity?.pricingSummary?.priceRange) {
            const minPrice = entity.pricingSummary.priceRange.min;
            
            return (
              <Tooltip title={`${entityName} - ₹${minPrice} to ₹${entity.pricingSummary.priceRange.max}`}>
                <Chip
                  key={value}
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CategoryIcon fontSize="small" />
                      <span>{entityName}</span>
                      <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        ₹{minPrice}
                      </Typography>
                    </Box>
                  }
                  size="small"
                  onDelete={readOnly ? undefined : () => handleDeleteChip(value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ 
                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                    border: '1px solid #2e7d32',
                    '& .MuiChip-deleteIcon': {
                      color: readOnly ? 'rgba(0, 0, 0, 0.26)' : '#2e7d32',
                      '&:hover': {
                        color: readOnly ? undefined : '#1b5e20',
                        backgroundColor: 'rgba(46, 125, 50, 0.2)'
                      }
                    }
                  }}
                />
              </Tooltip>
            );
          }
          break;
          
        case 'PLAN':
          if (entity?.price) {
            return (
              <Tooltip title={`${entityName} - $${entity.price}`}>
                <Chip
                  key={value}
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Description fontSize="small" />
                      <span>{entityName}</span>
                      <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        ${entity.price}
                      </Typography>
                    </Box>
                  }
                  size="small"
                  onDelete={readOnly ? undefined : () => handleDeleteChip(value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ 
                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                    border: '1px solid #2e7d32',
                    '& .MuiChip-deleteIcon': {
                      color: readOnly ? 'rgba(0, 0, 0, 0.26)' : '#2e7d32',
                      '&:hover': {
                        color: readOnly ? undefined : '#1b5e20',
                        backgroundColor: 'rgba(46, 125, 50, 0.2)'
                      }
                    }
                  }}
                />
              </Tooltip>
            );
          }
          break;
      }
    }
    
    // Simple chip without price for other cases
    return (
      <Chip
        key={value}
        label={entityName}
        size="small"
        onDelete={readOnly ? undefined : () => handleDeleteChip(value)}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{ 
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          '& .MuiChip-deleteIcon': {
            color: readOnly ? 'rgba(0, 0, 0, 0.26)' : 'rgb(25, 118, 210)',
            '&:hover': {
              color: readOnly ? undefined : '#1976d2'
            }
          }
        }}
      />
    );
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3, ...sx }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary">
          Loading {plural}...
          {finalDuration && finalCountry && ` (Duration: ${durationDisplay}, Country: ${countryDisplay})`}
        </Typography>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert 
        severity="error" 
        sx={{ mt: 1, ...sx }}
        action={
          <Box display="flex" gap={1}>
            <Button 
              size="small" 
              onClick={handleRetry}
              variant="outlined"
            >
              Retry
            </Button>
          </Box>
        }
      >
        <Box>
          <Typography variant="body2" gutterBottom>
            {fetchError}
          </Typography>
          {finalDuration && finalCountry && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Filters applied: Duration={finalDuration}, Country={finalCountry}
            </Typography>
          )}
        </Box>
      </Alert>
    );
  }

  if (entities.length === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ mt: 1, ...sx }}>
        <Typography variant="body2" gutterBottom>
          No {plural} available for selection.
        </Typography>
        {context === 'SUBSCRIPTION' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" component="div" color="textSecondary">
              {finalDuration && finalCountry 
                ? `No ${plural.toLowerCase()} found for the selected duration (${durationDisplay}) and country (${countryDisplay}).`
                : finalDuration
                ? `No ${plural.toLowerCase()} found for the selected duration (${durationDisplay}).`
                : finalCountry
                ? `No ${plural.toLowerCase()} found for the selected country.`
                : 'Make sure items have pricing configured.'}
            </Typography>
          </Box>
        )}
      </Alert>
    );
  }

  // ========== SINGLE SELECTION ==========
  if (finalSelectionType === 'single') {
    const selectedValue = selectedIds?.[0] || '';
    const selectedEntity = getEntityDetails(selectedValue);

    console.log("selected value : ", selectedValue);

    // Function to render entity details based on type
    const renderEntityDetails = () => {
      if (!selectedEntity) return null;
      
      if (context === 'SUBSCRIPTION') {
        switch (entityType.toUpperCase()) {
          case 'VALUEPACK':
            if (selectedEntity.tiers && selectedEntity.tiers.length > 0) {
              const currencySymbol = selectedEntity.tiers[0]?.currencySymbol || '$';
              
              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Selected {label} - All Tiers
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {selectedEntity.tiers
                      .sort((a, b) => {
                        const tierOrder = { 'Low': 1, 'Mid': 2, 'High': 3 };
                        return (tierOrder[a.tierType] || 0) - (tierOrder[b.tierType] || 0);
                      })
                      .map((tier, index) => (
                        <Box 
                          key={tier.id || index}
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            mb: 1,
                            bgcolor: tier.isPopular ? 'rgba(255, 193, 7, 0.1)' : 
                                     tier.isRecommended ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            borderRadius: 1,
                            border: '1px solid rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {tier.tierType} Tier
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {tier.description}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            {tier.discountType && tier.discountValue && tier.originalPrice && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through',
                                  color: 'text.disabled',
                                  display: 'block'
                                }}
                              >
                                {currencySymbol}{parseFloat(tier.originalPrice).toFixed(2)}
                              </Typography>
                            )}
                            
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                              {currencySymbol}{parseFloat(tier.currentPrice).toFixed(2)}
                            </Typography>
                            
                            {tier.discountType === 'percentage' && tier.discountValue && (
                              <Chip
                                size="small"
                                label={`${tier.discountValue}% OFF`}
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Box>
              );
            }
            break;
            
          case 'PRODUCT':
            if (selectedEntity.pricingSummary) {
              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Selected {label} Details
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedEntity.pricingSummary?.priceRange && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Price Range
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            ₹{selectedEntity.pricingSummary.priceRange.min} - ₹{selectedEntity.pricingSummary.priceRange.max}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Packages
                          </Typography>
                          <Typography variant="body2">
                            {selectedEntity.pricingSummary.packageCount || 0} packages
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              );
            }
            break;
            
          case 'PLAN':
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  Selected {label} Details
                </Typography>
                <Grid container spacing={1}>
                  {selectedEntity.price && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Price
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        ${selectedEntity.price}
                      </Typography>
                    </Grid>
                  )}
                  {selectedEntity.duration && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Duration
                      </Typography>
                      <Typography variant="body2">
                        {selectedEntity.duration} months
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
        }
      }
      
      return null;
    };

    return (
      <FormControl 
        fullWidth 
        error={!!error} 
        disabled={disabled || loading || readOnly}
        sx={sx}
        size={size}
        variant={variant}
      >
        <InputLabel id={`${context}-${entityType}-label`} shrink={!!selectedValue}>
          {label}
          {required && <span style={{ color: '#d32f2f' }}> *</span>}
        </InputLabel>
        <Select
          labelId={`${context}-${entityType}-label`}
          value={selectedValue}
          onChange={(e) => handleSingleSelection(e.target.value)}
          label={required ? `${label} *` : label}
          renderValue={(selected) => {
            if (!selected) return placeholder || `Select ${label.toLowerCase()}...`;
            
            const entity = getEntityDetails(selected);
            if (entity) {
              if (context === 'SUBSCRIPTION') {
                switch (entityType.toUpperCase()) {
                  case 'VALUEPACK':
                    if (entity.tiers && entity.tiers.length > 0) {
                      // const currencySymbol = entity.tiers[0]?.currencySymbol || '$';
                      const lowTier = entity.tiers.find(t => t.tierType === 'Low') || entity.tiers[0];
                      // const startingPrice = lowTier?.currentPrice ? parseFloat(lowTier.currentPrice).toFixed(2) : '0.00';
                      
                      return (
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Layers fontSize="small" color="primary" />
                            <Typography variant="body2">
                              {getEntityName(selected)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    break;
                    
                  case 'PRODUCT':
                    if (entity.pricingSummary?.priceRange) {
                      return (
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Box display="flex" alignItems="center" gap={1}>
                            <CategoryIcon fontSize="small" color="primary" />
                            <Typography variant="body2">
                              {getEntityName(selected)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    break;
                    
                  case 'PLAN':
                    if (entity.price) {
                      return (
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Description fontSize="small" color="primary" />
                            <Typography variant="body2">
                              {getEntityName(selected)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    break;
                }
              }
              
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {getEntityName(selected)}
                  </Typography>
                </Box>
              );
            }
            return getEntityName(selected);
          }}
          input={<OutlinedInput label={required ? `${label} *` : label} />}
          MenuProps={{
            PaperProps: {
              sx: { 
                maxHeight: 400,
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }
              }
            }
          }}
        >
          <MenuItem value="">
            <em>{placeholder || `Select ${label.toLowerCase()}...`}</em>
          </MenuItem>
          
          {entities.map((entity) => (
            <MenuItem key={entity.id} value={entity.id}>
              {renderEntityDisplay(entity)}
            </MenuItem>
          ))}
        </Select>
        
        {showDescription && description && (
          <FormHelperText sx={{ mt: 0.5, color: 'text.secondary' }}>
            {description}
          </FormHelperText>
        )}
        
        {helperText && (
          <FormHelperText error={!!error}>
            {helperText}
          </FormHelperText>
        )}
        
        {renderEntityDetails()}
        
        {selectedEntity && finalDuration && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary" display="block">
              Applied Filters
            </Typography>
            <Box display="flex" gap={1} mt={0.5}>
              {finalDuration && durationDisplay && (
                <Chip size="small" label={`Duration: ${durationDisplay}`} color="info" variant="outlined" />
              )}
              {finalCountry && countryDisplay && (
                <Chip size="small" label={`Country: ${countryDisplay}`} color="info" variant="outlined" />
              )}
            </Box>
          </Grid>
        )}
      </FormControl>
    );
  }

  // ========== MULTIPLE SELECTION ==========
  const selectedValues = selectedIds || [];
  
  return (
    <FormControl 
      fullWidth 
      error={!!error} 
      disabled={disabled || loading || readOnly}
      sx={sx}
      size={size}
      variant={variant}
    >
      <InputLabel 
        id={`${context}-${entityType}-label`}
        shrink={selectedValues.length > 0}
      >
        {label}
        {required && <span style={{ color: '#d32f2f' }}> *</span>}
      </InputLabel>
      <Select
        labelId={`${context}-${entityType}-label`}
        multiple
        value={selectedValues}
        onChange={(e) => handleMultipleChange(e.target.value)}
        input={<OutlinedInput label={required ? `${label} *` : label} />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return placeholder || `Select ${label.toLowerCase()}...`;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              {selected.map((value) => renderSelectedChip(value))}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: { 
              maxHeight: 400,
              '& .MuiMenuItem-root': {
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                '&:last-child': {
                  borderBottom: 'none'
                }
              }
            }
          }
        }}
      >
        <MenuItem value="" disabled>
          <em>{placeholder || `Select ${label.toLowerCase()}...`}</em>
        </MenuItem>
        
        {entities.map((entity) => (
          <MenuItem key={entity.id} value={entity.id}>
            <Checkbox 
              checked={selectedValues.includes(entity.id)} 
              size="small"
              disabled={readOnly}
              sx={{ mr: 1 }}
            />
            <ListItemText 
              primary={renderEntityDisplay(entity)}
              sx={{ ml: 1 }}
            />
          </MenuItem>
        ))}
      </Select>
      
      {showDescription && description && (
        <FormHelperText sx={{ mt: 0.5, color: 'text.secondary' }}>
          {description}
        </FormHelperText>
      )}
      
      {helperText && (
        <FormHelperText error={!!error}>
          {helperText}
        </FormHelperText> 
      )}
      
      {selectedValues.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 1 }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="textSecondary">
              {selectedValues.length} {selectedValues.length === 1 ? 'item' : 'items'} selected
            </Typography>
            
            {context === 'SUBSCRIPTION' && (
              <Box display="flex" alignItems="center" gap={1}>
                {finalDuration && finalCountry && (
                  <Typography variant="caption" color="info.main">
                    Filtered: {durationDisplay || finalDuration}, {countryDisplay || finalCountry}
                  </Typography>
                )}
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                  Total: {selectedValues.length} {plural}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </FormControl>
  );
};

export default ModularEntitySelector;
